import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import api from '../lib/api';
import DetailSearchSort, { filterAndSortRows } from './DetailSearchSort';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0].slice(-2)}`;
  return d;
};

const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];
const sortColorWise = (variants) => {
  if (!variants?.length) return [];
  const colorOrder = [], colorMap = {};
  for (const v of variants) { if (!colorMap[v.color]) { colorMap[v.color] = []; colorOrder.push(v.color); } colorMap[v.color].push(v); }
  return colorOrder.sort((a, b) => (a || '').localeCompare(b || '')).flatMap(c => colorMap[c].sort((a, b) => SIZE_ORDER.indexOf(a.size) - SIZE_ORDER.indexOf(b.size)));
};

const STATUS_COLORS = { confirmed: 'bg-blue-100 text-blue-700', partial_received: 'bg-amber-100 text-amber-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', closed: 'bg-gray-200 text-gray-700', draft: 'bg-slate-100 text-slate-600' };
const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const PurchaseOrderDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [searchDesign, setSearchDesign] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareFormat, setShareFormat] = useState('pdf');
  const [shareMode, setShareMode] = useState('order_form');
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [cancellingItem, setCancellingItem] = useState(null);
  const [cancelReason, setCancelReason] = useState('Fabric unavailable');
  const [cancelNotes, setCancelNotes] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());

  const toggleItemSelect = (itemId) => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };

  const selectableItems = (po?.items || []).filter(i => i.status !== 'cancelled');

  const toggleSelectAll = () => {
    if (selectedItemIds.size === selectableItems.length && selectableItems.length > 0) {
      setSelectedItemIds(new Set());
    } else {
      setSelectedItemIds(new Set(selectableItems.map(i => i.id)));
    }
  };

  const downloadSelectedBarcodes = async () => {
    if (selectedItemIds.size === 0) { toast.error('Select at least one design'); return; }
    try {
      // Use the custom barcode endpoint with selected items
      const res = await api.post('/purchase-orders/barcode-excel-selected', {
        po_id: po.id,
        item_ids: Array.from(selectedItemIds),
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a'); a.href = url; a.download = `barcodes-${po.po_number}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Barcode Excel downloaded!');
    } catch { toast.error('Failed to download'); }
  };

  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const CANCEL_REASONS = ['Fabric unavailable', 'Design rejected', 'Cost too high', 'Vendor unable to deliver', 'Customer cancelled', 'Duplicate entry', 'Other'];

  const loadData = useCallback(async () => {
    try {
      const [poRes, vendRes, brandRes] = await Promise.all([
        api.get(`/purchase-orders/${id}`), api.get('/vendors'), api.get('/brands'),
      ]);
      setPo(poRes.data);
      setVendors(vendRes.data);
      setBrands(brandRes.data);
    } catch { toast.error('Failed to load PO'); navigate('/purchase-orders'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const getVendorName = (vid) => {
    const v = vendors.find(x => x.id === vid);
    if (!v) return 'Unknown';
    const name = v.name || v.company_name || 'Unknown';
    const loc = v.city?.toLowerCase() === 'mumbai' ? v.area : v.city;
    return loc ? `${name} (${loc})` : name;
  };
  const getBrandName = (bid) => brands.find(b => b.id === bid)?.name || '';

  const totalOrdered = (po?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (parseInt(v.qty_ordered) || 0), 0), 0);
  const totalReceived = (po?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (parseInt(v.qty_received) || 0), 0), 0);
  // Pending = sum of per-variant pending (extra received on one design doesn't offset others)
  const totalPending = (po?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + Math.max(0, (parseInt(v.qty_ordered) || 0) - (parseInt(v.qty_received) || 0)), 0), 0);
  const pct = totalOrdered > 0 ? Math.round(totalReceived / totalOrdered * 100) : 0;

  const deletePO = async () => {
    if (!window.confirm('Delete this Purchase Order? This cannot be undone.')) return;
    try { await api.delete(`/purchase-orders/${po.id}`); toast.success('PO deleted'); navigate('/purchase-orders'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const executeShare = async (format, action) => {
    const endpoint = format === 'pdf' ? `/purchase-orders/${po.id}/pdf?mode=${shareMode}` : `/purchase-orders/${po.id}/excel?mode=${shareMode}`;
    const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const ext = format === 'pdf' ? '.pdf' : '.xlsx';
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      if (action === 'whatsapp') {
        const file = new File([res.data], `${po.po_number}${ext}`, { type: mimeType });
        if (navigator.share) { await navigator.share({ files: [file], title: po.po_number }); }
        else { window.open(`https://wa.me/?text=${encodeURIComponent(`PO: ${po.po_number}`)}`); }
      } else {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
        const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}${ext}`; a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success(`${format.toUpperCase()} ${action === 'whatsapp' ? 'shared' : 'downloaded'}!`);
    } catch { toast.error('Failed'); }
    setShareOpen(false);
  };

  const executeWhatsAppImage = async () => {
    try {
      const res = await api.get(`/purchase-orders/${po.id}/image`, { responseType: 'blob' });
      const file = new File([res.data], `${po.po_number}_summary.png`, { type: 'image/png' });
      if (navigator.share) {
        await navigator.share({ files: [file], title: `${po.po_number} Summary` });
      } else {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'image/png' }));
        const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}_summary.png`; a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success('WhatsApp image ready!');
    } catch { toast.error('Failed to generate image'); }
    setShareOpen(false);
  };

  const downloadLabels = async (type) => {
    try {
      const endpoint = type === 'excel' ? `/purchase-orders/${po.id}/barcode-labels-excel` : `/purchase-orders/${po.id}/barcode-labels`;
      const ext = type === 'excel' ? '.xlsx' : '.pdf';
      const mime = type === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      if (type === 'view') { window.open(url, '_blank'); }
      else { const a = document.createElement('a'); a.href = url; a.download = `${po.po_number}_labels${ext}`; a.click(); window.URL.revokeObjectURL(url); }
      toast.success('Labels ready');
    } catch { toast.error('Failed to generate labels'); }
    setLabelsOpen(false);
  };

  const cancelPOItem = async () => {
    try {
      await api.post(`/purchase-orders/${cancellingItem.poId}/items/${cancellingItem.itemId}/cancel`, { reason: cancelReason, notes: cancelNotes });
      toast.success('Item cancelled');
      setCancellingItem(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!po) return null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 animate-fadeIn" data-testid="po-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/purchase-orders')} data-testid="po-detail-back" className="text-gray-500 hover:text-gray-900">
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 font-mono" data-testid="po-detail-number">{po.po_number}</h1>
              <Badge className={`${STATUS_COLORS[po.status] || 'bg-gray-100'} text-sm`} data-testid="po-detail-status">{statusLabel(po.status)}</Badge>
            </div>
            <h2 className="text-xl font-bold text-blue-800 mt-1" data-testid="po-detail-vendor">{getVendorName(po.vendor_id)}</h2>
            <div className="flex items-center gap-3 flex-wrap mt-1 text-sm text-gray-500">
              <span>{fmtDate(po.po_date)}</span>
              {(() => { const v = vendors.find(x => x.id === po.vendor_id); return v ? (
                <>{v.city && <span>{v.city}{v.state ? `, ${v.state}` : ''}</span>}</>
              ) : null; })()}
              {po.dispatch_date && <span>Dispatch: {fmtDate(po.dispatch_date)}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {po.approval_status === 'approved' && (<>
            <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} data-testid="po-detail-share">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Download
            </Button>
            <Button size="sm" variant="outline" onClick={() => setLabelsOpen(true)} data-testid="po-detail-labels">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
              Labels
            </Button>
          </>)}
          {canManage && (<>
            <Button size="sm" variant="outline" className="text-blue-600 border-blue-300" onClick={() => navigate('/create-purchase-order', { state: { editPO: po } })} data-testid="po-detail-edit">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              Edit
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={deletePO} data-testid="po-detail-delete">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Delete
            </Button>
          </>)}
        </div>
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="po-detail-summary">
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Total Amount</p><p className="text-xl font-bold text-gray-900">₹{(po.total_amount || 0).toLocaleString('en-IN')}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Ordered</p><p className="text-xl font-bold text-blue-700">{totalOrdered} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Received</p><p className="text-xl font-bold text-green-700">{totalReceived} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">{po.status === 'closed' ? 'Short Closed' : 'Pending'}</p><p className={`text-xl font-bold ${po.status === 'closed' ? 'text-orange-600' : 'text-red-600'}`}>{totalPending} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Progress</p>
          <p className="text-xl font-bold text-emerald-700">{pct}%</p>
          <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1.5"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, pct)}%` }}></div></div>
        </CardContent></Card>
      </div>

      {/* Purchase Entry CTA */}
      {canManage && po.status !== 'cancelled' && po.status !== 'completed' && po.status !== 'closed' && po.approval_status === 'approved' && (
        <div className="flex gap-3 flex-wrap">
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/create-purchase-entry', { state: { fromPO: { vendor_id: po.vendor_id, po_id: po.id, po_number: po.po_number, items: po.items } } })} data-testid="po-detail-create-pe">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            Create Purchase Entry
          </Button>
          {(po.status === 'partial_received' || po.status === 'confirmed') && totalPending > 0 && (
            <Button variant="outline" className="border-orange-400 text-orange-700" onClick={async () => {
              if (!window.confirm(`Close balance of ${totalPending} pcs?`)) return;
              try { await api.post(`/purchase-orders/${po.id}/close-balance`); toast.success('Balance closed!'); loadData(); }
              catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
            }} data-testid="po-detail-close-balance">Close Balance ({totalPending} pcs)</Button>
          )}
        </div>
      )}

      {/* PO Info */}
      {(po.transport_name || po.notes || po.rejection_reason || po.created_by_name) && (
        <div className="flex gap-6 flex-wrap text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          {po.transport_name && <span>Transport: <strong>{po.transport_name}</strong></span>}
          {po.created_by_name && <span>Created by: <strong>{po.created_by_name}</strong></span>}
          {po.created_at && <span>Created: <strong>{new Date(po.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong></span>}
          {po.notes && <span className="italic text-gray-500">Notes: {po.notes}</span>}
          {po.rejection_reason && <span className="text-red-600">Rejection: {po.rejection_reason}</span>}
        </div>
      )}

      {/* Items — Excel-like pivot table (matching download format) */}
      {(() => {
        const allItems = po.items || [];
        // Gather all used sizes across all items
        const usedSizes = new Set();
        allItems.forEach(item => (item.variants || []).forEach(v => usedSizes.add(v.size)));
        const activeSizes = SIZE_ORDER.filter(s => usedSizes.has(s));
        if (activeSizes.length === 0) activeSizes.push('M', 'L', 'XL', '2XL');

        // Build rows: each row = one item+color combo
        let sr = 0;
        let grandQty = 0, grandAmount = 0;
        const rows = [];
        allItems.forEach((item, idx) => {
          const colorMap = {};
          const colorOrder = [];
          (item.variants || []).forEach(v => {
            const c = v.color || 'Other';
            if (!colorMap[c]) { colorMap[c] = { sizes: {}, qty: 0, received: {}, costs: {} }; colorOrder.push(c); }
            colorMap[c].sizes[v.size] = (colorMap[c].sizes[v.size] || 0) + (v.qty_ordered || 0);
            colorMap[c].received[v.size] = (colorMap[c].received[v.size] || 0) + (v.qty_received || 0);
            colorMap[c].qty += (v.qty_ordered || 0);
            colorMap[c].costs[v.size] = v.tentative_cost || 0;
          });
          colorOrder.forEach(color => {
            sr++;
            const d = colorMap[color];
            // Calculate correct amount using per-size costs
            let amount = 0;
            activeSizes.forEach(s => { amount += (d.sizes[s] || 0) * (d.costs[s] || 0); });
            grandQty += d.qty;
            grandAmount += amount;
            const totalReceived = Object.values(d.received).reduce((s, v) => s + v, 0);

            // Build cost groups: group consecutive sizes with same cost
            const costGroups = [];
            let currentGroup = null;
            activeSizes.forEach(s => {
              if ((d.sizes[s] || 0) === 0) return; // skip sizes not in this color
              const cost = d.costs[s] || 0;
              if (currentGroup && currentGroup.cost === cost) {
                currentGroup.sizes.push(s);
              } else {
                currentGroup = { cost, sizes: [s] };
                costGroups.push(currentGroup);
              }
            });

            rows.push({ sr, item, idx, color, sizes: d.sizes, received: d.received, costs: d.costs, qty: d.qty, amount, delivery: item.delivery_date, totalReceived, costGroups });
          });
        });

        return (
          <Card data-testid="po-detail-items">
            <CardContent className="p-0">
              <DetailSearchSort search={searchDesign} setSearch={setSearchDesign} sortBy={sortBy} setSortBy={setSortBy} sortOptions={['rate', 'amount']} testIdPrefix="po-detail" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="po-excel-table">
                  <thead>
                    <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                      <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-10">
                        <input type="checkbox" className="w-4 h-4 accent-emerald-500"
                          checked={selectedItemIds.size > 0 && selectedItemIds.size === selectableItems.length}
                          onChange={toggleSelectAll} data-testid="po-select-all" />
                      </th>
                      <th className="px-3 py-2.5 text-center font-medium border border-gray-700 w-10">SR</th>
                      <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-16">Image</th>
                      <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Design</th>
                      <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Brand</th>
                      <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Colour</th>
                      {activeSizes.map(s => (
                        <th key={s} className="px-2 py-2.5 text-center font-medium border border-gray-700 min-w-[42px]">{s}</th>
                      ))}
                      <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Qty</th>
                      <th className="px-3 py-2.5 text-right font-medium border border-gray-700">Amount</th>
                      <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Received</th>
                      <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Delivery</th>
                    </tr>
                  </thead>
                <tbody>
                    {(() => {
                      // Group consecutive rows by item ref so we can rowSpan
                      // SR/Image/Design/Brand across all colour rows of a design.
                      const groups = [];
                      let currentGrp = null;
                      rows.forEach((r, ri) => {
                        if (!currentGrp || currentGrp.itemRef !== r.item) {
                          currentGrp = { itemRef: r.item, startIdx: ri, count: 0, sr: groups.length + 1 };
                          groups.push(currentGrp);
                        }
                        currentGrp.count++;
                      });
                      const infoFor = (ri) => {
                        const grp = groups.find(g => g.startIdx <= ri && ri < g.startIdx + g.count);
                        return {
                          grp,
                          isFirst: grp.startIdx === ri,
                          isLast: grp.startIdx + grp.count - 1 === ri,
                        };
                      };
                      return filterAndSortRows(rows, searchDesign, sortBy).map((r, ri) => {
                      const info = infoFor(ri);
                      const isCancelled = r.item.status === 'cancelled';
                      const firstImg = (r.item.images || []).find((img, imgI) => {
                        const ic = r.item.image_colors || {};
                        const imgColor = ic[img];
                        return imgColor ? imgColor.toLowerCase() === r.color.toLowerCase() : imgI === 0;
                      }) || (r.item.images || [])[0];
                      // Thick separator between designs: applied to the cost row of the last colour
                      const designEndBorder = info.isLast ? 'border-b-[3px] border-b-[#1E1E23]' : '';
                      const designSpan = info.grp.count * 2; // each colour = main row + cost row

                      return (
                        <React.Fragment key={ri}>
                        {/* Main data row */}
                        <tr className={`${isCancelled ? 'opacity-50 line-through' : ''} ${selectedItemIds.has(r.item.id) ? 'bg-emerald-50' : 'bg-white'} hover:bg-emerald-50/40 transition-colors`}>
                          {info.isFirst && (
                            <td className="px-2 py-2 text-center border border-gray-500 align-middle" rowSpan={designSpan}>
                              <input type="checkbox" className="w-4 h-4 accent-emerald-500"
                                checked={selectedItemIds.has(r.item.id)}
                                onChange={() => toggleItemSelect(r.item.id)}
                                data-testid={`po-item-check-${r.item.id}`} />
                            </td>
                          )}
                          {info.isFirst && (
                            <td className="px-3 py-2 text-center text-gray-500 border border-gray-500 font-mono text-xs" rowSpan={designSpan}>{info.grp.sr}</td>
                          )}
                          {info.isFirst && (
                            <td className="px-2 py-1.5 text-center border border-gray-500" rowSpan={designSpan}>
                              {firstImg ? (
                                <img
                                  src={`${API_BASE}${firstImg}`}
                                  alt={r.item.design_code}
                                  className="w-11 h-11 rounded object-cover cursor-pointer hover:ring-2 hover:ring-emerald-400 transition-all mx-auto"
                                  onClick={() => setLightboxImg(`${API_BASE}${firstImg}`)}
                                  data-testid={`po-excel-img-${ri}`}
                                />
                              ) : (
                                <div className="w-11 h-11 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                </div>
                              )}
                            </td>
                          )}
                          {info.isFirst && (
                            <td className="px-3 py-2 font-semibold text-gray-900 border border-gray-500" rowSpan={designSpan}>{r.item.design_code}</td>
                          )}
                          {info.isFirst && (
                            <td className="px-3 py-2 text-gray-600 border border-gray-500 text-xs" rowSpan={designSpan}>{getBrandName(r.item.brand_id)}</td>
                          )}
                          <td className="px-3 py-2 font-medium text-gray-800 border border-gray-500" rowSpan="2">{r.color}</td>
                          {activeSizes.map(s => {
                            const q = r.sizes[s] || 0;
                            const rec = r.received[s] || 0;
                            const done = rec >= q && q > 0;
                            return (
                              <td key={s} className="px-2 py-2 text-center border border-gray-500">
                                {q > 0 ? (
                                  <div>
                                    <span className={`font-medium ${done ? 'text-green-600' : ''}`}>{q}</span>
                                    {rec > 0 && <div className={`font-medium ${done ? 'text-green-600' : 'text-amber-500'}`}>{rec}(R)</div>}
                                  </div>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 text-center font-bold text-gray-900 border border-gray-500" rowSpan="2">{r.qty}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.amount > 0 ? `₹${r.amount.toLocaleString('en-IN')}` : ''}</td>
                          <td className="px-3 py-2 text-center border border-gray-500" rowSpan="2">
                            <span className={`font-medium ${r.totalReceived >= r.qty && r.qty > 0 ? 'text-green-600' : r.totalReceived > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{r.totalReceived}</span>
                          </td>
                          <td className="px-3 py-2 text-center text-xs border border-gray-500" rowSpan="2">{r.delivery ? <span className="text-orange-600 font-medium">{fmtDate(r.delivery)}</span> : '-'}</td>
                        </tr>
                        {/* Cost group row — merged cells by common cost */}
                        <tr className={`${isCancelled ? 'opacity-50' : ''} bg-white ${designEndBorder}`}>
                          {(() => {
                            const cells = [];
                            let sizeIdx = 0;
                            while (sizeIdx < activeSizes.length) {
                              const s = activeSizes[sizeIdx];
                              const q = r.sizes[s] || 0;
                              if (q === 0) {
                                cells.push(<td key={sizeIdx} className="border border-gray-500"></td>);
                                sizeIdx++;
                                continue;
                              }
                              // Find the cost group this size belongs to
                              const group = r.costGroups.find(g => g.sizes.includes(s));
                              if (group) {
                                // Find how many consecutive activeSizes columns this group spans
                                let span = 0;
                                for (let i = sizeIdx; i < activeSizes.length; i++) {
                                  if (group.sizes.includes(activeSizes[i])) span++;
                                  else break;
                                }
                                cells.push(
                                  <td key={sizeIdx} colSpan={span} className="px-2 py-1.5 text-center border border-gray-500">
                                    <span className="font-semibold text-blue-800 text-xs">@₹{group.cost.toLocaleString('en-IN')}</span>
                                  </td>
                                );
                                sizeIdx += span;
                              } else {
                                cells.push(<td key={sizeIdx} className="border border-gray-500"></td>);
                                sizeIdx++;
                              }
                            }
                            return cells;
                          })()}
                        </tr>
                        </React.Fragment>
                      );
                    });
                    })()}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#218C59] text-white font-bold text-sm">
                      <td className="px-2 py-2.5 border border-green-700"></td>
                      <td className="px-3 py-2.5 border border-green-700" colSpan="5">TOTAL</td>
                      {activeSizes.map(s => {
                        const sizeTotal = rows.reduce((sum, r) => sum + (r.sizes[s] || 0), 0);
                        return <td key={s} className="px-2 py-2.5 text-center border border-green-700">{sizeTotal || ''}</td>;
                      })}
                      <td className="px-3 py-2.5 text-center border border-green-700">{grandQty}</td>
                      <td className="px-3 py-2.5 text-right border border-green-700">₹{grandAmount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2.5 text-center border border-green-700">{rows.reduce((s, r) => s + r.totalReceived, 0)}</td>
                      <td className="px-3 py-2.5 border border-green-700"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Floating Action Bar for selected items */}
      {selectedItemIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-4 animate-fadeIn" data-testid="po-item-action-bar">
          <span className="text-sm font-medium">{selectedItemIds.size} design{selectedItemIds.size > 1 ? 's' : ''} selected</span>
          <Button size="sm" variant="ghost" className="text-green-300 hover:text-green-200 hover:bg-green-900/30 gap-1.5"
            onClick={downloadSelectedBarcodes} data-testid="po-selected-barcode-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Download Barcode Excel
          </Button>
          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white" onClick={() => setSelectedItemIds(new Set())} data-testid="po-clear-selection">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </Button>
        </div>
      )}

      {/* Image Lightbox */}
      <Dialog open={!!lightboxImg} onOpenChange={() => setLightboxImg(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-none" data-testid="image-lightbox" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          <img src={lightboxImg} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" />
        </DialogContent>
      </Dialog>

      {/* Share/Download Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Download / Share</DialogTitle><DialogDescription>Choose type, format, and action</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Mode Selection */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Download Type</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setShareMode('order_form')}
                  className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${shareMode === 'order_form' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`}
                  data-testid="share-mode-order-form"
                >
                  Order Form
                </button>
                <button
                  onClick={() => setShareMode('with_received')}
                  className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${shareMode === 'with_received' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                  data-testid="share-mode-with-received"
                >
                  With Received
                </button>
                <button
                  onClick={() => setShareMode('pending_only')}
                  className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${shareMode === 'pending_only' ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`}
                  data-testid="share-mode-pending-only"
                >
                  Pending Only
                </button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">
                {shareMode === 'order_form' && 'Clean order form for vendor sharing'}
                {shareMode === 'with_received' && 'Full status with received quantities'}
                {shareMode === 'pending_only' && 'Only items with pending delivery'}
              </p>
            </div>
            {/* Format Selection */}
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Format</p>
              <div className="flex gap-3">
                <Button variant={shareFormat === 'pdf' ? 'default' : 'outline'} className={shareFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setShareFormat('pdf')} data-testid="share-format-pdf">PDF</Button>
                <Button variant={shareFormat === 'excel' ? 'default' : 'outline'} className={shareFormat === 'excel' ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setShareFormat('excel')} data-testid="share-format-excel">Excel</Button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Both formats include a Cost Summary</p>
            </div>
            {/* Actions */}
            <div className="flex gap-3">
              <Button className="flex-1" onClick={() => executeShare(shareFormat, 'download')} data-testid="po-share-download">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                Download
              </Button>
              <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => executeShare(shareFormat, 'whatsapp')} data-testid="po-share-whatsapp">
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
                WhatsApp
              </Button>
            </div>
            {/* WhatsApp Image Separator */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-2 text-gray-400">or share as image</span></div>
            </div>
            {/* WhatsApp Image */}
            <Button variant="outline" className="w-full border-green-300 text-green-700 hover:bg-green-50" onClick={executeWhatsAppImage} data-testid="po-share-wa-image">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              WhatsApp Image (PNG Summary)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Labels Dialog */}
      <Dialog open={labelsOpen} onOpenChange={setLabelsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Barcode Labels</DialogTitle><DialogDescription>Generate barcode labels</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <Button className="w-full justify-start" variant="outline" onClick={() => downloadLabels('view')} data-testid="po-labels-view">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              Open in Browser
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => downloadLabels('pdf')} data-testid="po-labels-pdf">
              <svg className="w-4 h-4 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Download PDF
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => downloadLabels('excel')} data-testid="po-labels-excel">
              <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Download Excel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Item Dialog */}
      <Dialog open={!!cancellingItem} onOpenChange={v => { if (!v) setCancellingItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Cancel PO Item</DialogTitle><DialogDescription>Provide a reason</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Reason *</Label>
              <select value={cancelReason} onChange={e => setCancelReason(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="cancel-item-reason">
                {CANCEL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={cancelNotes} onChange={e => setCancelNotes(e.target.value)} placeholder="Additional notes..." data-testid="cancel-item-notes" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setCancellingItem(null)}>Go Back</Button>
              <Button onClick={cancelPOItem} className="bg-red-600 hover:bg-red-700" data-testid="confirm-cancel-item">Confirm Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrderDetail;
